/**
 * «Registrar consentimiento» / «Quitar consentimiento» desde la ficha de un contacto (SPEC-BD §4.3, §6.3).
 *
 * Registrar pide cómo, cuándo, la constancia y el motivo: es la prueba del art. 5 de la Ley 25.326 y
 * queda en el historial con quién y cuándo. Quitar pide sólo el motivo. Las reglas son las del backend
 * (`validarConsentimientoManual`): el botón no se habilita con el formulario a medias.
 *
 * Un 409 `EN_NO_CONTACTAR` muestra el texto del backend con el enlace a No contactar: la baja no se
 * levanta desde acá.
 */

import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";
import type { CambioConsentimiento } from "../../types.base";
import { hoyLocal, validarConsentimientoManual } from "../../logic/base";
import { FUENTES_CONSENTIMIENTO, FUENTE_OTRA, fuenteElegida } from "./comun";

const MAX_MOTIVO = 500;

interface Props {
  modo: "registrar" | "quitar";
  busy: boolean;
  /** Texto del error que devolvió el backend. */
  error: string;
  /** El error fue EN_NO_CONTACTAR: se suma el enlace a la lista. */
  enNoContactar: boolean;
  onConfirmar: (c: CambioConsentimiento) => void;
  onCancelar: () => void;
}

export function ModalConsentimiento({ modo, busy, error, enNoContactar, onConfirmar, onCancelar }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [opcion, setOpcion] = useState("");
  const [otra, setOtra] = useState("");
  const [fecha, setFecha] = useState("");
  const [constancia, setConstancia] = useState(false);
  const [motivo, setMotivo] = useState("");
  const hoy = hoyLocal();
  const ids = { titulo: useId(), fuente: useId(), otra: useId(), fecha: useId(), constancia: useId(), motivo: useId() };
  const registrar = modo === "registrar";

  const fuente = fuenteElegida(opcion, otra);
  const v = validarConsentimientoManual({ optIn: registrar, fuente, fecha, constancia, motivo }, hoy);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onCancelar(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancelar]);

  const enviar = () => {
    if (!v.ok || busy) return;
    const m = motivo.trim();
    onConfirmar(registrar ? { optIn: true, fuente, fecha, constancia: true, motivo: m } : { optIn: false, motivo: m });
  };

  const campo = "w-full mt-1.5 h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4">
      <div ref={trapRef} role="dialog" aria-modal="true" aria-labelledby={ids.titulo} className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 id={ids.titulo} className="font-semibold text-lg text-slate-900">{registrar ? "Registrar consentimiento" : "Quitar consentimiento"}</h3>
          <button onClick={onCancelar} disabled={busy} aria-label="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-50">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {registrar ? (
            <>
              <div>
                <label htmlFor={ids.fuente} className="text-xs font-semibold text-slate-600">Cómo lo dio</label>
                <select id={ids.fuente} value={opcion} disabled={busy} onChange={(e) => setOpcion(e.target.value)} className={campo}>
                  <option value="">Elegí una opción</option>
                  {FUENTES_CONSENTIMIENTO.map((f) => <option key={f} value={f}>{f}</option>)}
                  <option value={FUENTE_OTRA}>{FUENTE_OTRA}</option>
                </select>
              </div>
              {opcion === FUENTE_OTRA && (
                <div>
                  <label htmlFor={ids.otra} className="text-xs font-semibold text-slate-600">Contá cómo lo dio</label>
                  <input id={ids.otra} value={otra} maxLength={100} disabled={busy} onChange={(e) => setOtra(e.target.value.slice(0, 100))} className={campo} />
                </div>
              )}
              <div>
                <label htmlFor={ids.fecha} className="text-xs font-semibold text-slate-600">Cuándo</label>
                <input id={ids.fecha} type="date" value={fecha} max={hoy} disabled={busy} onChange={(e) => setFecha(e.target.value)} className={campo} />
              </div>
              <label htmlFor={ids.constancia} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input id={ids.constancia} type="checkbox" checked={constancia} disabled={busy} onChange={(e) => setConstancia(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                Tengo constancia de este consentimiento y la puedo mostrar si alguien reclama
              </label>
            </>
          ) : (
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <span>Si tiene mensajes en cola en una campaña que todavía no salió, no se le mandan.</span>
            </div>
          )}

          <div>
            <label htmlFor={ids.motivo} className="text-xs font-semibold text-slate-600">Motivo del cambio (queda registrado)</label>
            <textarea id={ids.motivo} value={motivo} rows={3} maxLength={MAX_MOTIVO} disabled={busy}
              onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO))}
              className="w-full mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50" />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{motivo.length} / {MAX_MOTIVO}</p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              <p>{error}</p>
              {enNoContactar && <Link to="/activa/oficial/bajas" className="mt-1 inline-block font-semibold underline">Ir a Bajas</Link>}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button onClick={onCancelar} disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Volver
          </button>
          <button onClick={enviar} disabled={!v.ok || busy} title={v.ok ? undefined : v.error}
            className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white disabled:opacity-50 disabled:cursor-not-allowed ${
              registrar ? "bg-violet-600 hover:bg-violet-700" : "bg-red-600 hover:bg-red-700"
            }`}>
            {busy && <Loader2 size={14} className="animate-spin" />}
            {registrar ? "Registrar consentimiento" : "Quitar consentimiento"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalConsentimiento;
